/**
 * RSMA — Relative-Strength Moving-Average cross. Ported from the MPP C++
 * `AfiatEquiScan_RSMA_Function` (this indicator has no chart study, so per the
 * "use the file for things not implemented" rule it comes straight from there).
 *
 *   RS    = close / referenceClose   (reference = SPY)
 *   RSMA  = EMA of RS with smoothing 2/15, seeded at the first RS
 *   signal: a +1 count starts when RS crosses ABOVE its RSMA (and increments
 *           each subsequent bar) until RS crosses BELOW (which starts a -1
 *           count). The magnitude is the bars-since-cross (the "age"); the sign
 *           is the regime. 0 before the first cross.
 *
 * The shared series returned is the SIGNED COUNT; the state def takes its sign,
 * the age def its magnitude. `needsComparative` → the scanner supplies
 * `compareCloses` (SPY, date-aligned to the symbol's bars).
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DW, TF_PARAM } from "./timeframe";
import type { OHLCV, SharedIndicatorDef, SharedParam } from "../types";
import { resampleByLastClose, htIndexPerDay } from "./weekly";
import { maFull, signedMaCrossAge, MA_TYPE_CHOICES } from "./ta";

/** The three index-aligned RSMA series from an RS ratio series:
 *  - `count` = signed cross-count (drives Trend/Age)
 *  - `rs`    = the RS ratio itself (the Value)
 *  - `rsma`  = its EMA, the line RS crosses (the MA). */
type RsParts = { count: (number | null)[]; rs: (number | null)[]; rsma: (number | null)[] };

// Default MA = EMA(14): α = 2/(14+1) = 2/15, the canonical RSMA smoothing. The
// configurable indicators pass a length + MA type; the scanner preset columns
// and the chart RS family call with no args → unchanged EMA(14)/2-15 behavior.
const DEFAULT_MA_LEN = 14;
function rsmaParts(
  rsd: ReadonlyArray<number | null>,
  length: number = DEFAULT_MA_LEN,
  maType = 0,
): RsParts {
  const n = rsd.length;
  const count: (number | null)[] = new Array(n).fill(null);
  const rsma: (number | null)[] = new Array(n).fill(null);
  let i0 = 0;
  while (i0 < n && (rsd[i0] == null || !Number.isFinite(rsd[i0]!))) i0++;
  if (i0 >= n) return { count, rs: rsd.slice(), rsma };

  // Forward-fill from the first valid bar (RS nulls are rare once SPY is
  // date-aligned), build a dense series, take its MA, then the signed cross-age.
  const m = n - i0;
  const dense = new Array<number>(m);
  let last = rsd[i0]!;
  for (let j = 0; j < m; j++) {
    const v = rsd[i0 + j];
    if (v != null && Number.isFinite(v)) last = v;
    dense[j] = last;
  }
  const line = maFull(dense, length, maType);
  const age = signedMaCrossAge(dense, line);
  for (let j = 0; j < m; j++) {
    rsma[i0 + j] = line[j]!;
    count[i0 + j] = age[j]!;
  }
  return { count, rs: rsd.slice(), rsma };
}

/** Daily RS ratio (close / SPY) per bar. */
function dailyRsd(data: OHLCV): (number | null)[] {
  const { closes, compareCloses } = data;
  const n = closes.length;
  if (!compareCloses || compareCloses.length !== n) return new Array(n).fill(null);
  return closes.map((c, i) => {
    const ref = compareCloses[i]!;
    return ref > 0 && Number.isFinite(ref) ? c / ref : null;
  });
}

/** Daily RSMA parts: signed count + RS value + RSMA line. */
export function rsmaDailyParts(data: OHLCV, length?: number, maType?: number): RsParts {
  return rsmaParts(dailyRsd(data), length, maType);
}
/** Daily RSMA signed count (close/SPY). Optional MA length + type (default
 *  EMA/14 = the canonical RSMA) for the configurable indicator. */
export function rsmaDaily(data: OHLCV, length?: number, maType?: number): (number | null)[] {
  return rsmaDailyParts(data, length, maType).count;
}

/** Weekly RSMA parts, each series projected onto daily indices. Inclusive of
 *  the in-progress week so the scanner's latest row counts the current weekly
 *  bar (RSMA has no chart study, so no lookahead-off needed). */
export function rsmaWeeklyParts(data: OHLCV, length?: number, maType?: number): RsParts {
  const { closes, compareCloses, times } = data;
  const n = closes.length;
  const empty = (): (number | null)[] => new Array(n).fill(null);
  if (!compareCloses || compareCloses.length !== n || !times || times.length !== n)
    return { count: empty(), rs: empty(), rsma: empty() };
  const sym = resampleByLastClose(times, closes, "week");
  const ref = resampleByLastClose(times, compareCloses, "week");
  const m = Math.min(sym.htCloses.length, ref.htCloses.length);
  if (m < 16) return { count: empty(), rs: empty(), rsma: empty() };
  const wRsd: (number | null)[] = new Array(m);
  for (let i = 0; i < m; i++) {
    const r = ref.htCloses[i]!;
    wRsd[i] = r > 0 && Number.isFinite(r) ? sym.htCloses[i]! / r : null;
  }
  const wParts = rsmaParts(wRsd, length, maType);
  const idxPerDay = htIndexPerDay(times, sym.htTimes.slice(0, m));
  const proj = (arr: (number | null)[]): (number | null)[] =>
    times.map((_, i) => {
      const k = idxPerDay[i]!;
      return k >= 0 && k < m ? arr[k]! : null;
    });
  return { count: proj(wParts.count), rs: proj(wParts.rs), rsma: proj(wParts.rsma) };
}
/** Weekly RSMA signed count, projected onto daily indices. Optional MA length +
 *  type (default EMA/14) for the configurable indicator. */
export function rsmaWeekly(data: OHLCV, length?: number, maType?: number): (number | null)[] {
  return rsmaWeeklyParts(data, length, maType).count;
}

const stateOf = (count: (number | null)[]): (number | null)[] =>
  count.map((v) => (v == null ? null : v > 0 ? 1 : v < 0 ? -1 : 0));

const RS_LABELS = { pos: "Bull", neg: "Bear", zero: "—" } as const;
// The whole RS family lives in its own "Momentum - RS" group (mirrors the PMC
// family). Per timeframe the order is Trend · Age · Value · MA.
const RS_GROUP = familyGroup("rs");

/** Shared params for the configurable "MA Age" indicators (RS + OBV): the MA
 *  length and type. Default EMA/14 reproduces the canonical 2/15 RSMA. */
export const MA_AGE_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "length", label: "MA Length", kind: "number", default: 14, min: 2, max: 200, step: 1 },
  { id: "maType", label: "MA Type", kind: "select", default: 0, choices: MA_TYPE_CHOICES },
];

// One def per concept + a Timeframe select (default Daily). The ids keep their
// historical "-daily" spelling (reused as canonical) so saved daily columns need
// no migration; only the weekly ids alias. Trend/Age derive from the signed
// rs_*_age column; Value/MA read panelKey columns. Weekly stays instant via the
// tf-resolver in dynamicIndicatorColumns.
export const RSMA_DAILY: SharedIndicatorDef = {
  id: "mpp-rsma-daily",
  label: "Afiat - RS vs Comparative",
  group: RS_GROUP,
  kind: "state",
  format: "state",
  stateLabels: RS_LABELS,
  needsComparative: true,
  params: [TF_DW, ...MA_AGE_PARAMS],
  computeSeries: (data, p) =>
    stateOf(p[TF_PARAM] === 1 ? rsmaWeekly(data, p.length, p.maType) : rsmaDaily(data, p.length, p.maType)),
};

/** Unsigned bars/weeks since the RSMA cross (a count); direction is RS Trend.
 *  Configurable MA Length + Type (default EMA/14 = the canonical 2/15). */
export const RSMA_DAILY_AGE: SharedIndicatorDef = {
  id: "mpp-rsma-daily-age",
  label: "Afiat - RS vs Comparative Age",
  group: RS_GROUP,
  format: "age",
  needsComparative: true,
  noLiveOverlay: true,
  params: [TF_DW, ...MA_AGE_PARAMS],
  computeSeries: (data, p) =>
    (p[TF_PARAM] === 1 ? rsmaWeekly(data, p.length, p.maType) : rsmaDaily(data, p.length, p.maType)).map(
      (v) => (v == null ? null : Math.abs(v)),
    ),
};

/** Raw RS ratio (close / SPY) — the RS value itself (in-progress week on Weekly). */
export const RS_DAILY_VALUE: SharedIndicatorDef = {
  id: "mpp-rs-daily-value",
  label: "Afiat - RS vs Comparative",
  group: RS_GROUP,
  format: "ratio",
  needsComparative: true,
  panelKey: "rs_d_value",
  params: [TF_DW],
  computeSeries: (data, p) => (p[TF_PARAM] === 1 ? rsmaWeeklyParts(data).rs : rsmaDailyParts(data).rs),
};

/** RSMA line — the MA the RS crosses (default EMA(2/15); configurable). */
export const RS_DAILY_MA: SharedIndicatorDef = {
  id: "mpp-rs-daily-ma",
  label: "Afiat - RS vs Comparative",
  group: RS_GROUP,
  format: "ratio",
  needsComparative: true,
  panelKey: "rs_d_ma",
  params: [TF_DW, ...MA_AGE_PARAMS],
  computeSeries: (data, p) =>
    (p[TF_PARAM] === 1 ? rsmaWeeklyParts(data, p.length, p.maType) : rsmaDailyParts(data, p.length, p.maType)).rsma,
};

/** IBD-style RS Rating (1–99) — the symbol's trailing weighted return
 *  (0.4·3mo + 0.2·6/9/12mo) percentile-ranked across the WHOLE tracked
 *  universe. Cross-sectional, so the scanner indicator endpoint computes it
 *  over the full universe (see `computeRsRatingAll`); `computeSeries` is a
 *  no-op placeholder (never called for cross-sectional defs). The four
 *  trailing-window weights are configurable (percent inputs; default = the IBD
 *  40/20/20/20 blend) — the endpoint reads them straight off these params. The
 *  rank is scale-invariant, so only the ratios between the weights matter.
 *  Mirrors the chart's RS Rating. */
export const RS_RATING: SharedIndicatorDef = {
  id: "rs-rating",
  label: "Afiat - Relative Strength Rank",
  group: RS_GROUP,
  format: "int",
  crossSectional: true,
  noLiveOverlay: true, // cross-sectional + monthly returns → batch cadence only
  params: [
    { id: "w3", label: "3mo weight %", kind: "number", default: 40, min: 0, max: 100, step: 5 },
    { id: "w6", label: "6mo weight %", kind: "number", default: 20, min: 0, max: 100, step: 5 },
    { id: "w9", label: "9mo weight %", kind: "number", default: 20, min: 0, max: 100, step: 5 },
    { id: "w12", label: "12mo weight %", kind: "number", default: 20, min: 0, max: 100, step: 5 },
  ],
  computeSeries: (data) => new Array(data.closes.length).fill(null),
};

export const RSMA_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  RS_RATING,
  RSMA_DAILY,
  RSMA_DAILY_AGE,
  RS_DAILY_VALUE,
  RS_DAILY_MA,
];
